import React, { forwardRef } from 'react';

import BayTooltip from '../../../BayTooltip';
import useMediaQuery from '../../../utilities/hook/useMediaQuery';

// Same breakpoint BayTooltip uses to suppress hover tooltips on touch devices.
const TOUCH_BREAKPOINT_QUERY = '(max-width: 991px)';

interface TabProps {
	id: string;
	classNames: string;
	selected: boolean;
	panelId: string;
	onFocus: (key: number | string) => () => void;
	onBlur: () => void;
	onClick: (key: number | string, evt: React.MouseEvent) => void;
	originalKey: number | string;
	children?: React.ReactNode;
	onRemove?: (evt: React.MouseEvent) => void;
	allowRemove?: boolean;
	disabled?: boolean;
	isComingSoon?: boolean;
	/** Shown below the "Coming Soon" badge on hover/focus (tap below 992px). Only used when `isComingSoon` is set. */
	comingSoonTooltip?: React.ReactNode;
}

const Tab = forwardRef<HTMLDivElement, TabProps>((props, ref) => {
	const {
		id,
		classNames,
		selected,
		panelId,
		onFocus,
		onBlur,
		onClick,
		originalKey,
		children = undefined,
		onRemove = () => {},
		allowRemove = false,
		disabled = false,
		isComingSoon = false,
		comingSoonTooltip = undefined,
	} = props;

	// BayTooltip drops hover tooltips below 992px, so the badge opens on tap there instead.
	const isTouchViewport = useMediaQuery(TOUCH_BREAKPOINT_QUERY);

	const onTabClick = (evt: React.MouseEvent) => {
		onClick(originalKey, evt);
	};

	const renderRemovableTab = () => {
		return (
			<div className="RRT__removable">
				<div className="RRT__removable-text">{children}</div>
				<div className="RRT__removable-icon" onClick={onRemove}>
					x
				</div>
			</div>
		);
	};

	const renderTab = () => {
		if (allowRemove) {
			return renderRemovableTab();
		}

		return children;
	};

	const renderComingSoonBadge = () => {
		const badgeClassName = 'py-1 px-2 fw-bold bg-yellow-200 rounded-1 text-yellow-900';

		if (comingSoonTooltip == null) {
			return (
				<div className={badgeClassName}>
					{' '}
					Coming Soon{' '}
				</div>
			);
		}

		// A native button when it opens a tooltip: focusable, keyboard-operable and announced
		// as interactive -- which matters most below 992px, where it opens on tap.
		const badge = (
			<button className={`${badgeClassName} border-0 cursor-pointer`} type="button">
				Coming Soon
			</button>
		);

		return (
			<BayTooltip
				overlay={comingSoonTooltip}
				overlayClassName="p-3"
				// Design is 360px wide with 12px padding (336px of text). `p-3` is 14px, the
				// nearest spacing step, so the box grows by 4px to keep the same text width.
				overlayMaxWidth={364}
				placement="bottom"
				trigger={isTouchViewport ? 'click' : 'hover'}
			>
				{badge}
			</BayTooltip>
		);
	};

	return isComingSoon ? (
		<div
			className={`d-flex align-items-center${disabled ? ' cursor-not-allowed' : ''}`}
		>
			<div
				ref={ref}
				aria-controls={panelId}
				aria-disabled={disabled ? 'true' : 'false'}
				aria-expanded={selected ? 'true' : 'false'}
				aria-selected={selected ? 'true' : 'false'}
				className={disabled ? `${classNames} text-body-secondary opacity-100` : classNames}
				id={id}
				role="tab"
				tabIndex={disabled ? -1 : 0}
			>
				{renderTab()}
			</div>
			{renderComingSoonBadge()}
		</div>
	) : (
		<div
			ref={ref}
			aria-controls={panelId}
			aria-disabled={disabled ? 'true' : 'false'}
			aria-expanded={selected ? 'true' : 'false'}
			aria-selected={selected ? 'true' : 'false'}
			className={classNames}
			id={id}
			role="tab"
			tabIndex={0}
			onBlur={onBlur}
			onClick={onTabClick}
			onFocus={onFocus(originalKey)}
		>
			{renderTab()}
		</div>
	);
});

export default Tab;
